/**
 * BSVAC / Triage time helpers.
 *
 * Every crew-facing timestamp is displayed in **Eastern Time**
 * (America/New_York, "ET") and rendered in **24-hour military time**,
 * regardless of the device's local timezone or clock preference. This
 * keeps radio traffic, PCR paperwork and Command Post KPIs internally
 * consistent even when an admin reviews records from another timezone.
 *
 * Note: input values that come from HTML `<input type="time">` are
 * already stored as 24-hour "HH:MM" strings — these helpers exist for
 * *display* transformations of ISO timestamps captured in UTC on the
 * backend.
 */

const ET = "America/New_York";

function _parts(iso) {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: ET,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const p = Object.fromEntries(fmt.formatToParts(d).map((x) => [x.type, x.value]));
  // Intl returns "24" for midnight on some browsers — normalize to "00".
  if (p.hour === "24") p.hour = "00";
  return p;
}

/** "MM/DD/YY · HH:MM ET" — used in list rows / command post cards. */
export function fmtDateTimeET(iso) {
  const p = _parts(iso);
  if (!p) return "—";
  return `${p.month}/${p.day}/${p.year.slice(-2)} · ${p.hour}:${p.minute} ET`;
}

/** "HH:MM:SS ET" — used for live clocks and "last seen" tickers. */
export function fmtTimeET(iso, { withSeconds = true } = {}) {
  const p = _parts(iso);
  if (!p) return "—";
  return withSeconds
    ? `${p.hour}:${p.minute}:${p.second} ET`
    : `${p.hour}:${p.minute} ET`;
}

/** "YYYY-MM-DD" for today in ET — used to default form_date pickers. */
export function todayIsoDateET() {
  const p = _parts(new Date());
  if (!p) return "";
  return `${p.year}-${p.month}-${p.day}`;
}

/** "HH:MM" (military) for right-now in ET — defaults for time pickers. */
export function nowHHMMET() {
  const p = _parts(new Date());
  if (!p) return "";
  return `${p.hour}:${p.minute}`;
}
